import React from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import CustomLabel from "../../components/CustomLabel";
import { useLocation, useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import { getAllReports } from "../../services/ReportsService";

const Reports: React.FC = () => {
  const [search, setSearch] = React.useState<string>("");
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });
  const location = useLocation();
  const params = useParams();
  const venueId = params.branchId;

  console.log("Reports component - params:", params);
  console.log("Reports component - venueId from params:", venueId);

  const [branches, setBranches] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >(["all"]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);
  const [reportsData, setReportsData] = React.useState<any[]>([]);
  const [loadingReports, setLoadingReports] = React.useState(false);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);

  React.useEffect(() => {
    const fetchReports = async () => {
      setLoadingReports(true);
      const isBranchSelected = !(
        selectValue === "" ||
        selectValue === null ||
        selectValue === undefined ||
        (Array.isArray(selectValue) && selectValue.length === 0)
      );

      if (!isBranchSelected) {
        console.log("No branch selected - skipping reports fetch");
        setReportsData([]);
        setLoadingReports(false);
        return;
      }
      try {
        let startDate: string | undefined;
        let endDate: string | undefined;

        if (dateRange) {
          if (Array.isArray(dateRange)) {
            startDate = dateRange[0];
            endDate = dateRange[1];
          } else {
            startDate = dateRange.start;
            endDate = dateRange.end;
          }
        }

        // Convert date strings to full ISO format for proper comparison
        if (startDate && /^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
          startDate = new Date(startDate + 'T00:00:00.000Z').toISOString();
        }
        if (endDate && /^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
          endDate = new Date(endDate + 'T23:59:59.999Z').toISOString();
        }

        const payload: any = {
          start_date: startDate,
          end_date: endDate,
        };

        const isAllSelected = Array.isArray(selectValue)
          ? selectValue.includes("all")
          : selectValue === "all";

        if (selectValue && !isAllSelected) {
          if (Array.isArray(selectValue)) {
            // For multi-select, we'll handle filtering client-side
            // or you can modify API to accept multiple IDs
          } else {
            payload.business_id = String(selectValue);
          }
        }

        const response = await getAllReports(payload, venueId ?? null);

        console.log("Reports fetched:", response);

        let processedData = [];
        if (response && response.expenses && response.profits) {
          const buildReportKey = (item: any) => {
            const businessId = item.business_id ?? item.businessId ?? "";
            const clientId = item.client_id ?? item.clientId ?? "";
            const appointmentId = item.appointment_id ?? item.appointmentId;
            const invoiceNumber =
              item.Invoice_number ?? item.invoice_number ?? item.invoiceNumber;
            const createdAt = item.created_at ?? item.createdAt ?? "";
            const title = item.title ?? "";
            const fallbackId = item.id ?? "";
            const reference = appointmentId ?? invoiceNumber ?? fallbackId;

            return [businessId, clientId, reference, createdAt, title].join(
              "|"
            );
          };

          const expenseMap = new Map();
          const profitMap = new Map();

          response.expenses.forEach((expense: any) => {
            const key = buildReportKey(expense);
            expenseMap.set(key, {
              id: expense.id,
              title: expense.title,
              description: expense.description,
              amount: parseFloat(expense.amount) || 0,
              date: expense.created_at,
              paid_at:
                expense.updated_at ??
                expense.update_at ??
                expense.paid_at ??
                null,
              notes: expense.notes,
              business_id: expense.business_id ?? expense.businessId ?? null,
              type: "expense",
            });
          });

          response.profits.forEach((profit: any) => {
            const key = buildReportKey(profit);
            profitMap.set(key, {
              id: profit.id,
              title: profit.title,
              description: profit.description,
              amount: parseFloat(profit.amount) || 0,
              date: profit.created_at,
              paid_at:
                profit.updated_at ??
                profit.update_at ??
                profit.paid_at ??
                null,
              notes: profit.notes,
              business_id: profit.business_id ?? profit.businessId ?? null,
              type: "profit",
            });
          });

          const allKeys = new Set([
            ...Array.from(expenseMap.keys()),
            ...Array.from(profitMap.keys()),
          ]);

          processedData = Array.from(allKeys).map((key) => {
            const expense = expenseMap.get(key);
            const profit = profitMap.get(key);

            const expenseAmount = expense ? expense.amount : 0;
            const profitAmount = profit ? profit.amount : 0;
            const netAmount = profitAmount - expenseAmount;

            const baseData = expense || profit;
            const paidAt =
              baseData.paid_at ??
              baseData.updated_at ??
              baseData.update_at ??
              null;

            return {
              id: baseData.id,
              title: baseData.title,
              description: baseData.description,
              amount: netAmount,
              date: baseData.date,
              paid_at: paidAt,
              notes: baseData.notes,
              business_id: baseData.business_id ?? baseData.businessId ?? null,
              expenseAmount: expenseAmount,
              profitAmount: profitAmount,
            };
          });
        } else {
          processedData = [];
          if (response && response.data) {
            processedData = Array.isArray(response.data)
              ? response.data
              : [response.data];
            processedData = processedData.map((item: any) => ({
              ...item,
              business_id: item.business_id ?? item.businessId ?? null,
              paid_at:
                item.paid_at ?? item.updated_at ?? item.update_at ?? null,
            }));
          } else if (Array.isArray(response)) {
            processedData = response;
          } else if (response) {
            processedData = [response];
          }
        }

        setReportsData(processedData);
      } catch (error) {
        console.error("Error fetching reports:", error);
        setReportsData([]);
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, [selectValue, dateRange, refreshTrigger]);
  React.useEffect(() => {
    const fetchBranches = async () => {
      console.log("fetchBranches called with venueId:", venueId);
      setLoadingBranches(true);
      try {
        if (!venueId) {
          console.error("No venue ID available, venueId:", venueId);
          setBranches([]);
          return;
        }

        const response = await getAllBranches(venueId);
        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        console.log("Processed branches data:", branchesData);
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [venueId]);
  const exportCsv = React.useCallback(() => {
    if (!reportsData || reportsData.length === 0) return;
    const keys = ["id", "title", "amount", "date"];
    const header = keys.join(",");
    const lines = reportsData.map((r: any) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reports-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [reportsData]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Reports"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
          selectConfig={{
            
           options: [
            { label: "All", value: "all" },
            ...branches.map((branch) => ({
              label: branch.name || "Unknown Branch",
              value: branch.business_id,
            })),
          ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select branch",
            size: "medium",
              label: "Select Branch",

            searchable: false,
            clearable: true,
            multiSelect: true,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              label: "Export CSV",
              onClick: () => Promise.resolve(exportCsv()),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          fields={[
            { key: "title", header: "Title" },
            {
              key: "amount",
              header: "Net Amount",
              transform: (r: any) =>
                typeof r.amount === "number"
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(r.amount)
                  : r.amount,
            },
            {
              key: "date",
              header: "Appointment At",
              transform: (r: any) => {
                const value = r.date ?? r.created_at ?? r.createdAt ?? null;
                const d = new Date(value);
                return Number.isNaN(d.getTime())
                  ? String(value || "")
                  : d.toLocaleDateString();
              },
            },
            {
              key: "paid_at",
              header: "Created At",
              transform: (r: any) => {
                const value =
                  r.paid_at ?? r.updated_at ?? r.update_at ?? null;
                const d = new Date(value);
                return Number.isNaN(d.getTime())
                  ? String(value || "")
                  : d.toLocaleDateString();
              },
            },
          ]}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = reportsData.filter((r: any) => {
              if (selectValue && selectValue !== "all") {
                const itemBusinessId = r.business_id ?? r.businessId ?? null;
                
                if (Array.isArray(selectValue)) {
                  // Multi-select: check if item's business_id is in the array
                  if (!selectValue.includes("all") && !selectValue.includes(String(itemBusinessId))) {
                    return false;
                  }
                } else {
                  // Single select
                  if (String(itemBusinessId) !== String(selectValue)) {
                    return false;
                  }
                }
              }

              if (!normalizedQuery) return true;

              const haystack = [
                r.title,
                r.description,
                r.amount,
                r.date,
                r.paid_at,
                r.createdBy,
              ]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: "0 0 10px 10px",
            padding: "16px",
            marginTop: "-10px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CustomLabel
            text="Total Net Amount:"
            sx={{ color: COLORS.typographyColor }}
          />
          <CustomLabel
            text={new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(
              reportsData
                .filter((r: any) => {
                  if (selectValue && selectValue !== "all") {
                    const itemBusinessId =
                      r.business_id ?? r.businessId ?? null;
                    
                    if (Array.isArray(selectValue)) {
                      // Multi-select: check if item's business_id is in the array
                      if (!selectValue.includes("all") && !selectValue.includes(String(itemBusinessId))) {
                        return false;
                      }
                    } else {
                      // Single select
                      if (String(itemBusinessId) !== String(selectValue)) {
                        return false;
                      }
                    }
                  }
                  return true;
                })
                .filter((r: any) => {
                  if (!search) return true;
                  const normalizedQuery = String(search || "")
                    .toLowerCase()
                    .trim();
                  const haystack = [
                    r.title,
                    r.description,
                    r.amount,
                    r.date,
                    r.paid_at,
                    r.createdBy,
                  ]
                    .filter(Boolean)
                    .map((v: any) => String(v).toLowerCase())
                    .join(" ");
                  return haystack.includes(normalizedQuery);
                })
                .reduce((sum: number, r: any) => {
                  return sum + (typeof r.amount === "number" ? r.amount : 0);
                }, 0)
            )}
            fontWeight="bold"
            sx={{ color: COLORS.MainColor }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Reports;
